import React from 'react'
import { useLocalStorage } from 'react-use'

import BUILD_VERSION from 'getBuildVersion'
import { isPlainObject } from 'utils/obj'

export interface AppRefresh {
  hasNewVersion: boolean
  doRefreshCheck: () => void
  markAsRefreshed: () => void
}

export const AppRefreshContext = React.createContext<AppRefresh>({
  hasNewVersion: false,
  doRefreshCheck: () => {},
  markAsRefreshed: () => {}
})

// check every 15 min
const POLLING_INTERVAL = 15 * 60_000

export const AppRefreshProvider: React.FC = ({ children }) => {
  const [hasNewVersion, setHasNewVersion] = useLocalStorage(
    '_appHasNewVersion',
    false
  )

  // // TODO REMOVE WHEN DONE - for forcing refresh in local testing
  // const [testRefresh, setTestRefresh] = useLocalStorage('_appRefreshTest', 0)

  const refreshVersion = React.useCallback(async () => {
    // console.log('refreshVersion')
    const cacheBuster = Math.ceil(Date.now() / 1000)
    let json

    try {
      const res = await fetch(
        `${window.location.origin}/version.json?${cacheBuster}`
      )

      // console.log('refreshVersion', { res })
      if (res.ok) {
        json = await res.json()
      } else {
        throw new Error('request failed')
      }
    } catch (e) {
      // console.log('refreshVersion error', { e })
      json = false
    }

    // // TODO REMOVE WHEN DONE
    // if (testRefresh) {
    //   console.log('--- TESTING REFRESH')
    //   json.build += 1
    //   setTestRefresh(0)
    // }

    if (isPlainObject(json) && json.build > BUILD_VERSION) {
      // console.log('new build number', {
      //   old: BUILD_VERSION,
      //   new: json.build
      // })
      setHasNewVersion(true)
      // } else {
      //   console.log('same version', { cur: BUILD_VERSION, json })
    }
  }, [setHasNewVersion])

  // start polling for updates
  React.useEffect(() => {
    // console.log('starting refreshVersion intervall')
    refreshVersion()
    const interval = setInterval(() => refreshVersion(), POLLING_INTERVAL)
    return () => {
      clearInterval(interval)
    }
  }, [refreshVersion])

  // some locations in the code will cause an app refresh on their own, so they only need a way to update the refresh state
  const markAsRefreshed = React.useCallback(() => {
    // console.log('--- MARK AS REFRESHED')
    setHasNewVersion(false)
  }, [setHasNewVersion])

  // other parts of the code need to update the state and cause a reload
  const doRefreshCheck = React.useCallback(() => {
    if (hasNewVersion) {
      setHasNewVersion(false)
      // if we reload too quickly the above call doesn't have time to finish and update local storage
      setTimeout(() => {
        // console.info('REFRESHING WEBAPP')
        window.location.reload()
      }, 500)
    }
  }, [setHasNewVersion, hasNewVersion])

  return (
    <AppRefreshContext.Provider
      value={{
        hasNewVersion,
        doRefreshCheck,
        markAsRefreshed
      }}
    >
      {children}
    </AppRefreshContext.Provider>
  )
}
